"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Globe, Sparkles, Map, Heart } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import Image from "next/image";

const SparkleParticle = ({ delay }: any) => {
  const randomX = Math.random() * 100;
  const randomY = Math.random() * 100;

  return (
    <motion.div
      className="absolute w-2 h-2 bg-indigo-200 rounded-full shadow-lg shadow-indigo-200/50"
      style={{
        left: `${randomX}%`,
        top: `${randomY}%`,
      }}
      initial={{
        scale: 0,
        opacity: 0,
      }}
      animate={{
        scale: [0, 1, 0],
        opacity: [0, 0.8, 0],
        y: [0, -30],
      }}
      transition={{
        duration: 3,
        delay: delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
};

const SparklesBackground = () => {
  const sparkles = Array.from({ length: 69 }).map((_, i) => (
    <SparkleParticle key={i} delay={i * 0.2} />
  ));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {sparkles}
    </div>
  );
};

export function Hero() {
  const ref = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <div
      className="relative min-h-screen w-full justify-center items-center"
      ref={ref}
    >
      {/* Background wrapper - wrap both bg image and overlay in motion.div */}
      <motion.div
        style={{ opacity }}
        className="fixed inset-0 z-0 w-full h-screen"
      >
        <div
          className="fixed inset-0 w-full h-full"
          style={{
            backgroundImage: "url('/herobackground.webp')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            minHeight: "100vh",
            width: "100%",
          }}
        />
        <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-transparent via-black/50 to-black" />
      </motion.div>

      {/* Sparkles wrapper - add fade effect */}
      <motion.div style={{ opacity }}>
        <SparklesBackground />
      </motion.div>

      {/* Main content - keep existing scroll animations */}
      <div className="relative z-10 flex min-h-screen mx-auto max-w-7xl justify-center items-center px-4 sm:px-6 lg:px-8">
        <motion.div
          style={{ y, opacity }}
          className="w-full max-w-4xl flex items-center justify-center"
        >
          <div className="flex flex-col w-full py-12 lg:py-20">
            <div className="flex flex-col space-y-10 w-full">
              {/* Logo */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="flex justify-center items-center"
              >
                <div className="relative w-48 sm:w-64 md:w-80 lg:w-96">
                  <Image
                    src="/favicon.webp"
                    alt="Voyas Logo"
                    priority={true}
                    height={500}
                    width={500}
                    className="w-full h-auto drop-shadow-2xl"
                  />
                </div>
              </motion.div>

              {/* Headline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-center space-y-4"
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Plan Your Perfect Trip with{" "}
                  <span className="bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">
                    AI-Powered Intelligence
                  </span>
                </h1>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-200 leading-relaxed max-w-3xl mx-auto font-light">
                  Get personalized travel recommendations and complete itineraries crafted just for you
                </p>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              >
                <Link href="/create-trip" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto border-2 border-indigo-400 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-7 text-lg sm:text-xl rounded-full relative overflow-hidden group shadow-2xl hover:shadow-indigo-500/50 transition-all duration-300"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0"
                      animate={{
                        x: ["100%", "-100%"],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                    <span className="relative z-10 font-bold flex items-center gap-2">
                      Start Planning
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Button>
                </Link>

                <Link
                  href="/my-trips"
                  className="text-white/90 font-medium hover:text-white transition-colors text-base sm:text-lg underline decoration-2 underline-offset-4 decoration-indigo-300 hover:decoration-indigo-200"
                >
                  View my trips →
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default Hero;
